import { Stack, Tooltip } from '@mui/material';
import cn from 'classnames';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { ChatChannelType, IChatConversation } from 'src/@types/chat';
import { useAuthContext } from 'src/auth/useAuthContext';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { NavbarCustomAvatar } from 'src/components/custom-avatar';
import Iconify from 'src/components/iconify';
import { useChatContext } from 'src/provider/ChatContext';
import { useSelector } from 'src/redux/store';
import { getCurrentSection } from 'src/state';
import { useChatActionBarUIState } from '../Add/useChatActionBarUIState';
import {
  StyledBadge,
  StyledListContainer,
  StyledTopLeftIcon,
} from '../shared/styles';
import { Action } from '../shared/types';
import { useChatUnreadState } from './useChatUnreadState';

type Props = {
  toggleOpenAction: (action: Action, fromClose?: boolean) => void;
  showBorder?: boolean;
};

export const ChatUnread = ({ toggleOpenAction, showBorder }: Props) => {
  const location = useLocation();
  const { currentActiveChat, setCurrentActiveChat, setIsDrawer } =
    useChatContext();

  const channelsMap = useSelector((state) => state.chat.conversations.byId);

  const { chatIDList, deleteChatFromList } = useChatActionBarUIState();
  const { chatList, closeChat, addChatToList } = useChatUnreadState();

  const currentLocation = useMemo(
    () => getCurrentSection(location.pathname),
    [location.pathname],
  );

  const openedChatList = Object.keys(chatList);

  const unreadList = useMemo(
    () =>
      Object.values(channelsMap).reduce((acc, item) => {
        if (chatIDList.includes(item._id)) return acc;

        if (
          item.type === ChatChannelType.TEXT &&
          item.unreadMessages?.length &&
          !openedChatList.includes(item._id)
        ) {
          return [...acc, item];
        }

        return acc;
      }, [] as IChatConversation[]),
    [channelsMap, chatIDList, openedChatList],
  );

  const handleOpenChat = (
    e: React.MouseEvent<HTMLDivElement>,
    conversation: IChatConversation,
    addToList = false,
  ) => {
    e.stopPropagation();
    const sameChat = currentActiveChat === conversation._id;

    if (sameChat) {
      if (currentLocation !== 'communicate') {
        toggleOpenAction('chat', sameChat);
        setCurrentActiveChat('');
      } else {
        toggleOpenAction('chat', true);
      }
      return;
    }

    if (addToList) {
      addChatToList(conversation);
    }
    setCurrentActiveChat(conversation._id);
    setIsDrawer(true);
    toggleOpenAction('chat', sameChat);
  };

  if (!unreadList?.length && !chatIDList.length && !openedChatList.length)
    return null;

  return (
    <StyledListContainer className={cn({ showBorder })}>
      {currentLocation !== 'communicate' &&
        !!unreadList?.length &&
        unreadList?.map((conversation: IChatConversation) => (
          <ChatItem
            key={conversation._id}
            conversation={conversation}
            unread
            handleOpenChat={(e) => handleOpenChat(e, conversation, true)}
          />
        ))}
      {chatIDList?.map((chatId) => (
        <ChatItem
          key={chatId}
          conversation={channelsMap[chatId]}
          pinned
          handleRemoveChat={() => deleteChatFromList(chatId)}
          handleOpenChat={(e) => handleOpenChat(e, channelsMap[chatId])}
        />
      ))}
      {openedChatList?.map((chatId) => (
        <ChatItem
          key={chatId}
          conversation={channelsMap[chatId]}
          handleRemoveChat={() => closeChat(chatId)}
          handleOpenChat={(e) => handleOpenChat(e, channelsMap[chatId])}
        />
      ))}
    </StyledListContainer>
  );
};

type ChatItemProps = {
  conversation: IChatConversation;
  handleOpenChat: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleRemoveChat?: () => void;
  unread?: boolean;
  pinned?: boolean;
};

const ChatItem = ({
  conversation,
  handleOpenChat,
  handleRemoveChat = () => {},
  unread,
  pinned,
}: ChatItemProps) => {
  const { user } = useAuthContext();
  const usersById = useSelector((state) => state.workspace.usersById);

  const unreadCount = useMemo(() => {
    if (!conversation?.unreadMessages?.length) return 0;
    const count = conversation.unreadMessages.length;
    return count > 0 ? (count > 9 ? '+9' : count) : 1;
  }, [conversation?.unreadMessages?.length]);

  const otherUserAvatar = useMemo(() => {
    if (!conversation?.participants) return null;
    const otherUser = conversation.participants
      .map((p) => 'user' in p && usersById[p.user])
      .find((p) => p && p._id !== user?._id);
    if (!otherUser) return null;

    return (
      <CPAvatar
        key={otherUser._id}
        name={otherUser.name}
        picture={otherUser.picture}
        status={otherUser.status}
        mask
        size="small"
      />
    );
  }, [usersById, user?._id, conversation?.participants]);

  const Content = useMemo(
    () => (
      <div onClick={handleOpenChat}>
        {conversation?.p2p ? (
          otherUserAvatar
        ) : (
          <NavbarCustomAvatar
            key={conversation?._id}
            name={conversation?.name}
            src={conversation?.picture}
            icon="tabler:hash"
            active
            isBig
          />
        )}
      </div>
    ),
    [
      handleOpenChat,
      otherUserAvatar,
      conversation?._id,
      conversation?.name,
      conversation?.picture,
      conversation?.p2p,
    ],
  );

  if (!conversation) return null;

  return (
    <Tooltip
      title={conversation.name}
      key={conversation._id}
      placement="left"
      arrow
    >
      <StyledBadge
        color={unread ? 'error' : 'primary'}
        overlap="circular"
        badgeContent={unreadCount || (unread ? 1 : '')}
        invisible={!unread && !unreadCount}
        sx={{
          '& > .MuiBadge-badge': {
            border: (theme) => `1px solid ${theme.palette.background.default}`,
          },
          cursor: 'pointer',
        }}
      >
        {unread ? (
          Content
        ) : (
          <Stack
            position="relative"
            alignItems="center"
            justifyContent="center"
          >
            <StyledTopLeftIcon
              className={cn('icon', { pinned })}
              onClick={handleRemoveChat}
            >
              <Iconify
                icon={pinned ? 'tabler:pin' : 'tabler:x'}
                width={14}
                height={14}
              />
            </StyledTopLeftIcon>
            {Content}
          </Stack>
        )}
      </StyledBadge>
    </Tooltip>
  );
};
